import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { APP } from '@/constants/testIds';
import { toast } from 'sonner';
import { LifeBuoy, Send, ChevronDown, ChevronUp } from 'lucide-react';

const statusPill = { open: 'pill-high', in_progress: 'pill-medium', resolved: 'pill-low' };

const Support = () => {
  const [tickets, setTickets] = useState([]);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [openId, setOpenId] = useState(null);
  const [replyBody, setReplyBody] = useState('');
  const [sending, setSending] = useState(false);

  const load = async () => { const { data } = await api.get('/support'); setTickets(data); };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !message.trim()) { toast.error('Subject and message required'); return; }
    await api.post('/support', { subject, message });
    setSubject(''); setMessage('');
    toast.success('Ticket submitted');
    load();
  };

  const sendReply = async (id) => {
    if (!replyBody.trim()) { toast.error('Type a reply first'); return; }
    setSending(true);
    try {
      await api.post(`/support/${id}/reply`, { body: replyBody });
      setReplyBody('');
      toast.success('Reply sent');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-10">
      <header>
        <div className="label-tag mb-2">// Support</div>
        <h1 className="text-4xl md:text-5xl font-light tracking-tight">Request a security review</h1>
        <p className="text-[color:var(--text-secondary)] mt-2 max-w-2xl">
          Direct line to our technicians for incident response, Web PI investigations, or general questions.
        </p>
      </header>

      <form onSubmit={submit} className="panel p-6 space-y-4">
        <div className="label-tag flex items-center gap-2"><LifeBuoy size={14} /> // New Ticket</div>
        <div>
          <div className="label-tag mb-2">Subject</div>
          <input data-testid={APP.supportSubject} className="input-base" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Suspicious device on guest VLAN" />
        </div>
        <div>
          <div className="label-tag mb-2">Message</div>
          <textarea data-testid={APP.supportMessage} rows={6} className="input-base" value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Describe the incident, when it started, and any screenshots…" />
        </div>
        <button data-testid={APP.supportSubmit} type="submit" className="btn-primary">
          <Send size={14} /> Submit Ticket
        </button>
      </form>

      <section>
        <div className="label-tag mb-4">// Your Tickets</div>
        {tickets.length === 0 ? (
          <div className="panel p-6 text-[color:var(--text-secondary)] text-sm">No tickets yet.</div>
        ) : (
          <div className="space-y-3" data-testid="customer-tickets-list">
            {tickets.map((t) => {
              const isOpen = openId === t.ticket_id;
              const responses = t.responses || [];
              const unreadHint = responses.some((r) => r.author_role === 'admin');
              return (
                <div key={t.ticket_id} className="panel" data-testid="customer-ticket-card">
                  <button
                    type="button"
                    onClick={() => { setOpenId(isOpen ? null : t.ticket_id); setReplyBody(''); }}
                    className="w-full flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 sm:p-5 text-left hover:bg-[color:var(--bg-surface)] transition-colors"
                  >
                    <div className="min-w-0">
                      <div className="text-sm sm:text-base font-medium truncate">{t.subject}</div>
                      <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] mt-1">
                        {new Date(t.created_at).toLocaleString()}
                        {responses.length > 0 && (
                          <> · {responses.length} repl{responses.length === 1 ? 'y' : 'ies'}
                            {unreadHint && <span className="text-[color:var(--brand)] ml-2">· staff replied</span>}
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`pill ${statusPill[t.status] || 'pill-muted'}`}>{(t.status || 'open').replace('_', ' ')}</span>
                      {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </div>
                  </button>

                  {isOpen && (
                    <div className="border-t border-[color:var(--border)] p-4 sm:p-5 space-y-4">
                      <div className="panel-soft p-3 sm:p-4">
                        <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] mb-1">
                          You · {new Date(t.created_at).toLocaleString()}
                        </div>
                        <div className="text-sm whitespace-pre-wrap">{t.message}</div>
                      </div>

                      {responses.map((r) => (
                        <div
                          key={r.response_id}
                          className={`panel-soft p-3 sm:p-4 ${r.author_role === 'admin' ? 'border-l-2 border-l-[color:var(--brand)]' : ''}`}
                          data-testid={r.author_role === 'admin' ? 'support-admin-reply' : 'support-customer-reply'}
                        >
                          <div className="font-mono text-[10px] uppercase tracking-widest mb-1">
                            <span className={r.author_role === 'admin' ? 'text-[color:var(--brand)]' : 'text-[color:var(--text-secondary)]'}>
                              {r.author_role === 'admin' ? '/ SUPREME WEB STAFF' : 'You'}
                            </span>
                            <span className="text-[color:var(--text-muted)]"> · {new Date(r.created_at).toLocaleString()}</span>
                          </div>
                          <div className="text-sm whitespace-pre-wrap">{r.body}</div>
                        </div>
                      ))}

                      {t.status !== 'resolved' && (
                        <div>
                          <div className="label-tag mb-2">// Add Reply</div>
                          <textarea
                            rows={3}
                            value={replyBody}
                            onChange={(e) => setReplyBody(e.target.value)}
                            placeholder="Reply to staff…"
                            className="input-base"
                            data-testid="customer-ticket-reply-input"
                          />
                          <button
                            onClick={() => sendReply(t.ticket_id)}
                            disabled={sending}
                            className="btn-primary !py-2 !text-[11px] mt-3"
                            data-testid="customer-ticket-reply-send"
                          >
                            <Send size={12} /> {sending ? 'Sending…' : 'Send Reply'}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
};

export default Support;
